/**
 * DTF Town Coin Rewards — Frontend API Client
 *
 * Calls go through the Shopify App Proxy:
 *   /apps/coin-rewards/<path>  →  https://dash.dtftown.com/api/coins/<path>
 * Shopify automatically appends `signature` and `logged_in_customer_id`
 * to every proxied request, so the storefront never sees or sends any
 * secret. Same-origin fetch means cookies are forwarded too.
 */
(function() {
  'use strict';

  const API_BASE = '/apps/coin-rewards';
  const app = document.getElementById('coin-rewards-app');
  if (!app) return;

  const baseHeaders = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };

  async function apiGet(path) {
    const res = await fetch(API_BASE + path, {
      headers: baseHeaders,
      credentials: 'same-origin',
    });
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    return res.json();
  }

  async function apiPost(path, body = {}) {
    const res = await fetch(API_BASE + path, {
      method: 'POST',
      headers: baseHeaders,
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `API error: ${res.status}`);
    }
    return res.json();
  }

  async function apiPostMultipart(path, formData) {
    const res = await fetch(API_BASE + path, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `API error: ${res.status}`);
    }
    return res.json();
  }

  function escapeHtml(str) {
    return String(str ?? '').replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
  }

  // Tab navigation
  document.querySelectorAll('.coin-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      document.querySelectorAll('.coin-tab').forEach(t => t.classList.remove('active'));
      document.querySelectorAll('.coin-panel').forEach(p => p.classList.remove('active'));
      tab.classList.add('active');
      document.getElementById('panel-' + tab.dataset.tab).classList.add('active');
    });
  });

  // Load balance
  async function loadBalance() {
    try {
      const data = await apiGet('/balance');
      document.getElementById('coinBalance').textContent = data.total_available || 0;
      document.getElementById('coinDollarValue').textContent = `≈ $${data.dollar_value || '0.00'}`;
      document.getElementById('coinPending').textContent = data.pending || 0;
      document.getElementById('coinCampaign').textContent = data.campaign || 0;
      document.getElementById('coinTier').textContent = data.tier_label || 'Townie';
    } catch (e) {
      console.error('Failed to load balance:', e);
    }
  }

  // Load level (tier) progress
  async function loadTierProgress() {
    try {
      const data = await apiGet('/tier');
      const el = document.getElementById('tierProgress');
      el.innerHTML = `
        <div class="coin-tier-info">
          <h3>Your Level: <strong>${data.tier_label}</strong> (${data.multiplier}x multiplier)</h3>
          ${data.next_tier ? `
            <p>Spend $${(data.next_tier_min - data.yearly_spent).toFixed(0)} more this year to reach <strong>${data.next_tier_label}</strong></p>
            <div class="coin-progress-bar"><div class="coin-progress-fill" style="width:${data.progress_percent}%"></div></div>
          ` : '<p>You are at the highest level!</p>'}
        </div>
      `;
    } catch (e) {
      console.error('Failed to load level:', e);
    }
  }

  // Load social media actions. Each card now carries an inline submit form
  // (URL + optional screenshot), even when the customer is below the trust
  // threshold — those submissions land as `manual_review_only` so staff can
  // credit coins by hand from the dashboard.
  async function loadSocialActions() {
    try {
      const data = await apiGet('/actions');
      const grid = document.getElementById('socialActionsGrid');
      const socialActions = (data.actions || []).filter(a => a.category === 'social_media');

      if (!socialActions.length) {
        grid.innerHTML = '<p class="coin-loading">No social media actions available yet.</p>';
        return;
      }

      const trustBlocked = socialActions.some(a => !a.eligible && /trust/i.test(a.reason || ''));
      // Two banner shapes:
      //   1. phone verified → warm, personal "we trust you" thank-you card.
      //      Even if some actions are still gated by trust score, the verified
      //      phone is the meaningful trust signal — at this point the customer
      //      shouldn't keep seeing a "you're not trusted yet" lecture.
      //   2. otherwise → "here's how to unlock" CTA card (verify phone, etc).
      let banner = '';
      if (data.phone_verified) {
        banner = renderTrustedBanner(data);
      } else if (trustBlocked) {
        banner = renderTrustBanner(data);
      }

      // Group by platform/family so a 13-item card wall doesn't look
      // like a wall of identical buttons. Order matters — the most
      // valuable & most-completed groups go first. The `icon` is a
      // short letter mark shown inside a colored chip (CSS handles
      // the platform-specific tint via `data-platform`).
      const groups = [
        { key: 'google_maps', label: 'Google Maps',     icon: 'G',  desc: 'High-impact reviews — biggest coin payouts.' },
        { key: 'facebook',    label: 'Facebook',        icon: 'f',  desc: 'Share or review on Facebook.' },
        { key: 'instagram',   label: 'Instagram',       icon: 'IG', desc: 'Tag @dtftown in posts, reels, or stories.' },
        { key: 'tiktok',      label: 'TikTok',          icon: 'TT', desc: 'TikTok content with #dtftown.' },
        { key: 'youtube',     label: 'YouTube',         icon: 'YT', desc: 'Long-form videos and shorts.' },
        { key: 'yelp',        label: 'Yelp',            icon: 'Y',  desc: 'Review your local DTF Town store.' },
        { key: 'trustpilot',  label: 'Trustpilot',      icon: 'TP', desc: 'Industry-trusted public reviews.' },
        { key: 'other',       label: 'Other Platforms', icon: '•',  desc: 'Anything else worth sharing.' },
      ];

      const grouped = groups.map(g => {
        const items = socialActions.filter(a => {
          const platform = (a.rules && a.rules.platform) || 'other';
          return platform === g.key;
        });
        if (!items.length) return '';
        return `
          <section class="coin-action-group" data-platform="${g.key}">
            <header class="coin-action-group__header">
              <span class="coin-action-group__icon">${escapeHtml(g.icon)}</span>
              <div>
                <h3 class="coin-action-group__title">${escapeHtml(g.label)}</h3>
                <p class="coin-action-group__desc">${escapeHtml(g.desc)}</p>
              </div>
            </header>
            <div class="coin-action-group__grid">
              ${items.map(renderActionCard).join('')}
            </div>
          </section>`;
      }).filter(Boolean).join('');

      grid.innerHTML = banner + grouped;
    } catch (e) {
      document.getElementById('socialActionsGrid').innerHTML = '<p>Failed to load actions.</p>';
    }
  }

  // Friendly "thank you, we trust you" panel shown to customers who've
  // already verified their phone and cleared the trust gate. Replaces
  // the noisy "Unlock rewards" card that no longer applies. We pull
  // the first name from the storefront `customer` object so the
  // greeting feels personal — it's exposed on document.body via a data
  // attribute (see coin-rewards.liquid).
  // Warm, personal panel for customers who've cleared phone verification.
  // The old version was a one-liner that read like a status badge; this
  // one talks to the customer by first name, names their tier, and frames
  // submissions as "we already trust you, just send it over" instead of
  // "you are eligible to submit". The goal is to build a relationship,
  // not to communicate state.
  function renderTrustedBanner(data) {
    const firstName = (document.body.dataset.dtfFirstName || '').trim();
    const tierLabel = data.tier_label || 'Townie';
    const safeName = firstName ? escapeHtml(firstName) : '';
    const heading = safeName
      ? `Welcome back, ${safeName} — we&rsquo;ve got you.`
      : 'Welcome back — we&rsquo;ve got you.';
    const opener = safeName
      ? `${safeName}, you&rsquo;re part of the DTF Town family now.`
      : 'You&rsquo;re part of the DTF Town family now.';
    return `
      <div class="coin-trust-banner coin-trust-banner--ok">
        <div class="coin-trust-banner__title">${heading}</div>
        <div class="coin-trust-banner__body">
          <p>
            ${opener} Phone verified, identity confirmed &mdash; you&rsquo;re
            one of the <strong>${escapeHtml(tierLabel)}</strong> printers we
            count on, and we trust you. Whatever you submit below goes
            straight to a real person on our team, usually approved within
            a few hours.
          </p>
          <p class="coin-trust-banner__sub">
            Thanks for printing with us. Seriously &mdash; every review,
            every unboxing clip, every tagged story helps a small team in
            our shops keep building something we&rsquo;re proud of. Drop
            what you&rsquo;ve got below and we&rsquo;ll take care of the
            rest. ❤️
          </p>
        </div>
      </div>`;
  }

  function renderTrustBanner(data) {
    // Phone verification line: a verified phone is THE single biggest trust
    // boost, so we always lead with it. CTA jumps the user to the Profile
    // tab where the OTP form lives.
    const phoneLine = data.phone_verified
      ? '<li>Phone verified ✓</li>'
      : `<li>
          <strong>Verify your phone with a 6-digit code</strong> — biggest single trust boost.
          <button type="button" class="coin-trust-banner__cta" onclick="goToSmsConsent()">
            Verify phone now →
          </button>
        </li>`;
    return `
      <div class="coin-trust-banner">
        <div>
          <div class="coin-trust-banner__title">Unlock Reviews &amp; Social Media rewards</div>
          <div class="coin-trust-banner__body">
            Your trust level is <strong>${escapeHtml(data.trust_category || 'low')}</strong>. You can still submit proof below — our team will review and credit coins manually. To unlock auto-credit:
            <ul>
              ${phoneLine}
              <li>Place a few completed orders (each adds trust points)</li>
              <li>Keep your account active for at least 30 days</li>
            </ul>
          </div>
        </div>
      </div>`;
  }

  // Jump the customer straight to the Profile tab and focus the SMS phone
  // input. We trigger an actual click on the tab button so the existing tab
  // state machine handles all the show/hide work.
  window.goToSmsConsent = function() {
    const profileTab = document.querySelector('.coin-tab[data-tab="profile"]');
    if (profileTab) profileTab.click();
    setTimeout(() => {
      const input = document.getElementById('phoneInput_email_sms_consent');
      if (input) {
        input.focus();
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 250);
  };

  // Storefront-side cache of the customer's orders & shop locations so the
  // review form can render order/location dropdowns without hitting the
  // API every time we render an action card. Hydrated once per page load
  // by loadSocialContext() — see initAll().
  let socialContext = { orders: [], locations: [], used_orders_by_action: {} };
  let socialContextError = null;

  async function loadSocialContext() {
    try {
      socialContext = await apiGet('/social/context');
      socialContextError = null;
    } catch (e) {
      socialContext = { orders: [], locations: [], used_orders_by_action: {} };
      // Surface the error so reviewers know why the dropdowns are
      // empty (instead of silently rendering "No eligible orders" on
      // a customer who actually has 50 orders). The most common cause
      // is the storefront not being signed in, which the AppProxy
      // can't recover from on the server side.
      socialContextError = (e && e.message) ? e.message : 'context load failed';
      try { console.warn('[dtf-coins] social context load failed', e); } catch(_) {}
    }
  }

  function renderActionCard(a) {
    const reqs = (a.requirements || []).map(r =>
      `<li class="${r.met ? 'met' : ''}">${escapeHtml(r.label)}</li>`
    ).join('');

    const lockedClass = a.eligible ? '' : ' is-locked';
    const reasonRow = (!a.eligible && !a.allow_manual_submit)
      ? `<div class="coin-action-status">${escapeHtml(a.reason || 'Not available right now')}</div>`
      : '';

    const isReviewForm = !!(a.rules && a.rules.review_form);
    const requiresText = !!(a.rules && a.rules.requires_text);
    const optionalOrder = !!(a.rules && a.rules.optional_order_id);
    // "Photo upload"-style actions (action_key contains "_photo") are
    // image-only; the URL field would be misleading. Skip it for those.
    const isPhotoOnly = /(_photo|_photos)$/i.test(a.action_key || '') || /photo upload/i.test(a.name || '');
    const usedOrders = (socialContext.used_orders_by_action || {})[a.action_key] || [];

    // Star rating widget — radio buttons under the hood, label-driven UI.
    // Hidden unless this is a review-shaped action.
    const starsBlock = isReviewForm ? `
      <div class="coin-stars-row" data-stars-for="${escapeHtml(a.action_key)}" role="radiogroup" aria-label="Rating">
        <span class="coin-stars-label">Rating</span>
        <div class="coin-stars-input">
          ${[1,2,3,4,5].map(n => `
            <button type="button" class="coin-star-btn" data-star="${n}" aria-label="${n} star${n>1?'s':''}" onclick="window.coinSetStars(this, ${n})">★</button>
          `).join('')}
          <input type="hidden" name="rating" value="">
        </div>
      </div>` : '';

    // Order picker — required for review forms. Already-reviewed orders
    // come back disabled via socialContext.used_orders_by_action so the
    // customer can't double-submit per order.
    const orderOptions = (socialContext.orders || []).map(o => {
      const disabled = usedOrders.includes(o.id) ? ' disabled' : '';
      const suffix = usedOrders.includes(o.id) ? ' (already reviewed)' : '';
      const date = o.placed_at ? new Date(o.placed_at).toLocaleDateString() : '';
      return `<option value="${o.id}"${disabled}>${escapeHtml(o.label)} — $${o.total_price.toFixed(2)} ${date ? '· ' + date : ''}${suffix}</option>`;
    }).join('');
    let orderFallbackText = 'No eligible orders yet — place at least one order to unlock review submissions.';
    if (socialContextError) {
      orderFallbackText = 'We couldn\'t load your past orders. Make sure you\'re signed in to your DTF Town account, then refresh this page.';
    }
    const orderBlock = isReviewForm ? `
      <label class="coin-field-label">Which order is this review about?${optionalOrder ? '' : ' <span class="coin-required">*</span>'}
        <select name="order_id" class="coin-select"${optionalOrder ? '' : ' required'}>
          <option value="">${optionalOrder ? 'Optional — pick an order…' : 'Pick an order…'}</option>
          ${orderOptions || `<option value="" disabled>${escapeHtml(orderFallbackText)}</option>`}
        </select>
      </label>` : '';

    // Location picker — which physical store the experience happened at.
    // Every location from the Locations table is offered, including the
    // ones admins have flagged as Coming Soon. The API already sorts so
    // open stores come first (orderBy is_coming_soon, then name), and
    // coming-soon rows are visually tagged here so customers can tell
    // them apart at a glance.
    const locOptions = (socialContext.locations || [])
      .map(l => {
        const suffix = l.is_coming_soon ? ' (Coming Soon)' : '';
        return `<option value="${l.id}">${escapeHtml(l.name)}${suffix}</option>`;
      })
      .join('');
    const locBlock = isReviewForm ? `
      <label class="coin-field-label">Which store? <span class="coin-required">*</span>
        <select name="location_id" class="coin-select" required>
          <option value="">Select a location…</option>
          ${locOptions || '<option value="" disabled>Stores list unavailable — refresh the page.</option>'}
        </select>
      </label>` : '';

    // Publish-consent checkbox: customer agrees to have the review shown
    // on our /pages/reviews wall (initials only). Hidden checkbox value
    // is sent so we can default to false. For public-review actions
    // (Google Maps, Yelp, Trustpilot, etc.) the backend treats consent
    // as mandatory; we mirror that on the input with `required` and a
    // "(required)" hint so the customer doesn't hit a server error.
    const requiresConsent = !!(a.rules && a.rules.requires_publish_consent);
    const consentBlock = isReviewForm ? `
      <label class="coin-consent-row${requiresConsent ? ' is-required' : ''}">
        <input type="checkbox" name="publish_consent" value="1"${requiresConsent ? ' required' : ''}>
        <span>
          I'm okay with DTF Town publishing this review on the public reviews page (your name will be shown as initials only).
          ${requiresConsent ? '<small style="display:block;margin-top:4px;color:#b91c1c"><strong>Required</strong> to claim this reward.</small>' : ''}
        </span>
      </label>` : '';

    const helperText = isReviewForm
      ? (requiresText
        ? 'Required: rating, store, and a few sentences. Photo optional.'
        : 'Required: rating + store. Add a link, screenshot, or note if you want — none required.')
      : 'Submit any combination of a link, a description, and/or a screenshot. Our team reads every one before crediting coins.';

    // Notes are required on top of rating for "Store & Experience"
    // (the customer is sharing a story, not just a star). Otherwise
    // optional.
    const notesPlaceholder = isReviewForm
      ? (requiresText
        ? 'Tell us about your visit — what did you order, how was the pickup, was the team helpful?'
        : 'What did you think? What did you order? How was the pickup/delivery? (optional)')
      : 'Tell us about it — what platform, what you posted, anything that helps us verify.';

    // For public-review actions (Google Maps, Yelp, Facebook, Trustpilot)
    // we render a placeholder CTA block right under the location picker.
    // Once the customer picks a store its review_links from socialContext
    // populate this block with a "Write your review on X" button that
    // opens the platform in a new tab. The "Did you post your review?"
    // hint then nudges them to come back and upload a screenshot.
    const platformForCta = platformKeyFor(a.action_key);
    const reviewCtaBlock = isReviewForm && platformForCta
        ? `<div class="coin-review-cta" data-platform="${platformForCta}" style="display:none"></div>`
        : '';

    // GBP-match flow: customer tells us the display name they'll use on
    // Google so the dashboard can fuzzy-match the incoming GBP review
    // back to this claim. Replaces the screenshot upload entirely.
    const requiresGbpMatch = !!(a.rules && a.rules.requires_gbp_match);
    const reviewerNameBlock = requiresGbpMatch ? `
      <label class="coin-field-label">Your name on Google <span class="coin-required">*</span>
        <input type="text" name="claimed_reviewer_name" class="coin-text-input"
               placeholder="e.g. Bekir B. or Bekir Burak Torun" maxlength="160" required>
        <small style="display:block;margin-top:4px;color:#6b7280">
          Use the exact name shown on your Google profile so we can match your review automatically.
        </small>
      </label>` : '';

    const submitForm = a.allow_manual_submit
      ? `<form class="coin-action-submit${isReviewForm ? ' is-review-form' : ''}"
              data-action="${escapeHtml(a.action_key)}"
              data-review="${isReviewForm ? '1' : '0'}"
              data-requires-text="${requiresText ? '1' : '0'}"
              data-optional-order="${optionalOrder ? '1' : '0'}"
              data-gbp-match="${requiresGbpMatch ? '1' : '0'}"
              data-platform="${platformForCta || ''}"
              onsubmit="return false;">
          ${orderBlock}
          ${locBlock}
          ${reviewCtaBlock}
          ${reviewerNameBlock}
          ${starsBlock}
          ${isPhotoOnly ? '' : '<input type="url" name="proof_url" placeholder="Optional: link to your post (https://...)">'}
          <textarea name="notes" placeholder="${notesPlaceholder}" rows="3"${requiresText ? ' required' : ''}></textarea>
          ${requiresGbpMatch ? '' : '<input type="file" name="screenshot" accept="image/*">'}
          ${consentBlock}
          <small>${requiresGbpMatch
            ? "Required: your Google name + store + rating. We'll release your coins automatically once we spot your review on Google (usually within an hour). 7-day window."
            : helperText}</small>
          <button type="button" class="coin-btn coin-btn-primary" onclick="submitSocialActionForm(this)">
            ${requiresGbpMatch ? 'Submit & write review' : 'Submit for review'}
          </button>
          <div class="coin-action-submit-msg" style="display:none"></div>
        </form>`
      : '';

    // Compact "header" row: title left, coin badge right. Cards are
    // collapsed by default so the wall scans like a checklist rather
    // than a 600px-tall form per row. Customers click to expand only
    // the action they actually want to submit.
    const headerRow = `
      <div class="coin-action-card__head">
        <h4>${escapeHtml(a.name)}</h4>
        <div class="coin-action-coins">+${a.base_coins}</div>
      </div>`;

    const collapsibleBody = submitForm
      ? `<details class="coin-action-card__details">
           <summary class="coin-action-card__summary">
             <span class="coin-action-card__summary-label">${a.eligible ? 'Submit for review' : 'Submit (manual review)'}</span>
             <span class="coin-action-card__summary-chev" aria-hidden="true">▾</span>
           </summary>
           <div class="coin-action-card__panel">
             ${reqs ? `<ul class="coin-action-requirements">${reqs}</ul>` : ''}
             ${submitForm}
           </div>
         </details>`
      : (reqs ? `<ul class="coin-action-requirements">${reqs}</ul>` : '');

    return `
      <div class="coin-action-card${lockedClass}">
        ${headerRow}
        ${reasonRow}
        ${collapsibleBody}
      </div>
    `;
  }

  // Map an action_key like "google_maps_review_photo" to the platform
  // key we store deep-links under ("google_maps"). Returns null for
  // actions that don't have a public-review write flow.
  function platformKeyFor(actionKey) {
    if (!actionKey) return null;
    if (actionKey.indexOf('google_maps') === 0) return 'google_maps';
    if (actionKey.indexOf('yelp') === 0) return 'yelp';
    if (actionKey.indexOf('facebook') === 0) return 'facebook';
    if (actionKey.indexOf('trustpilot') === 0) return 'trustpilot';
    return null;
  }

  function platformLabel(key) {
    return {
      google_maps: 'Google Maps',
      yelp: 'Yelp',
      facebook: 'Facebook',
      trustpilot: 'Trustpilot',
    }[key] || 'the review site';
  }

  // Refresh the "Write your review on X" CTA inside a review form. Called
  // every time the location dropdown changes (or once on render). If the
  // selected store has a deep-link configured for this action's platform
  // we render a primary button + a "Did you post your review?" hint that
  // shows up after the customer comes back to the tab. Otherwise the CTA
  // stays hidden and the customer just uploads a screenshot like before.
  function refreshReviewCta(form) {
    const cta = form.querySelector('.coin-review-cta');
    if (!cta) return;
    const platform = cta.dataset.platform;
    if (!platform) { cta.style.display = 'none'; return; }
    const locSelect = form.querySelector('select[name="location_id"]');
    const locId = locSelect && locSelect.value ? parseInt(locSelect.value, 10) : null;
    if (!locId) { cta.style.display = 'none'; cta.innerHTML = ''; return; }

    const loc = (socialContext.locations || []).find(l => Number(l.id) === locId);
    const url = loc && loc.review_links ? loc.review_links[platform] : null;
    const label = platformLabel(platform);

    if (!url) {
      // No deep-link configured for this store/platform — surface a soft
      // hint so the customer knows what's expected without us pretending
      // we can route them to a non-existent URL.
      cta.style.display = '';
      cta.innerHTML = `
        <div style="background:#fef3c7;border:1px solid #fcd34d;border-radius:8px;padding:10px 12px;font-size:13px;color:#7a5a00">
          Write your review on <strong>${label}</strong> for ${loc ? loc.name : 'this store'}, then come back and upload a screenshot below.
        </div>`;
      return;
    }

    cta.style.display = '';
    cta.innerHTML = `
      <a class="coin-btn coin-btn-primary" target="_blank" rel="noopener" href="${url}"
         data-review-link style="display:inline-flex;align-items:center;gap:6px;margin-bottom:6px">
        Write your review on ${label} →
      </a>
      <div data-posted-hint style="display:none;background:#dcfce7;border:1px solid #86efac;border-radius:8px;padding:10px 12px;font-size:13px;color:#14532d;margin-top:4px">
        Welcome back! Once your review is live on ${label}, attach a screenshot below and submit so we can credit your coins.
      </div>`;

    // When the customer comes back to the tab after clicking the deep
    // link, reveal a "posted? upload screenshot below" hint. We don't
    // actually know if they posted — Google doesn't tell us — but the
    // nudge is enough to close the loop most of the time.
    const link = cta.querySelector('[data-review-link]');
    const hint = cta.querySelector('[data-posted-hint]');
    if (link && hint) {
      let opened = false;
      link.addEventListener('click', () => { opened = true; });
      window.addEventListener('focus', function onFocus() {
        if (!opened) return;
        opened = false;
        hint.style.display = '';
        window.removeEventListener('focus', onFocus);
      });
    }
  }

  // Wire up the location dropdown so the CTA refreshes whenever the
  // customer switches stores. Delegated so we only attach one listener
  // per form even if the action card re-renders.
  document.addEventListener('change', function (e) {
    const sel = e.target;
    if (sel && sel.matches('.coin-action-submit select[name="location_id"]')) {
      const form = sel.closest('.coin-action-submit');
      if (form) refreshReviewCta(form);
    }
  });

  // Star rating helper — toggles the active state on the row of buttons
  // and writes the chosen value into the hidden <input name="rating">.
  // Re-clicking the active star clears the rating (back to "no rating").
  window.coinSetStars = function(btn, value) {
    const wrap = btn.closest('.coin-stars-input');
    if (!wrap) return;
    const hidden = wrap.querySelector('input[name="rating"]');
    const buttons = wrap.querySelectorAll('.coin-star-btn');
    const current = parseInt(hidden.value, 10) || 0;
    const next = current === value ? 0 : value;
    hidden.value = next || '';
    buttons.forEach(b => {
      const n = parseInt(b.dataset.star, 10);
      b.classList.toggle('is-active', n <= next);
    });
  };

  // Load social submissions
  async function loadSocialSubmissions() {
    try {
      const data = await apiGet('/social/status');
      const list = document.getElementById('socialSubmissionsList');
      const items = data.submissions || [];

      if (!items.length) {
        list.innerHTML = '<p class="text-muted">No submissions yet.</p>';
        return;
      }

      list.innerHTML = items.map(s => `
        <div class="coin-submission-item">
          <div>
            <strong>${s.action}</strong>
            <small style="display:block;color:#999">${new Date(s.submitted_at).toLocaleDateString()}</small>
          </div>
          <div>
            <span class="coin-action-coins">${s.coin_value} coins</span>
            <span class="coin-submission-status ${s.status}">${s.status}</span>
          </div>
        </div>
      `).join('');
    } catch (e) {
      document.getElementById('socialSubmissionsList').innerHTML = '<p>Failed to load.</p>';
    }
  }

  // Submit social action via the inline card form. Any combination of
  // link, description text, and screenshot is accepted — at least one
  // must be present. When a screenshot is included we POST as multipart
  // so the backend can hash + dedupe it; otherwise we send plain JSON.
  window.submitSocialActionForm = async function(btn) {
    const form = btn.closest('.coin-action-submit');
    if (!form) return;
    const actionKey = form.dataset.action;
    const isReview = form.dataset.review === '1';
    const isGbpMatch = form.dataset.gbpMatch === '1';
    const urlInput = form.querySelector('input[type="url"]');
    const notesInput = form.querySelector('textarea[name="notes"]');
    const fileInput = form.querySelector('input[type="file"]');
    const orderSelect = form.querySelector('select[name="order_id"]');
    const locSelect = form.querySelector('select[name="location_id"]');
    const ratingInput = form.querySelector('input[name="rating"]');
    const consentInput = form.querySelector('input[name="publish_consent"]');
    const reviewerNameInput = form.querySelector('input[name="claimed_reviewer_name"]');
    const msg = form.querySelector('.coin-action-submit-msg');

    const url = (urlInput?.value || '').trim();
    const notes = (notesInput?.value || '').trim();
    const hasFile = !!fileInput?.files?.length;
    const orderId = orderSelect ? orderSelect.value : '';
    const locationId = locSelect ? locSelect.value : '';
    const rating = ratingInput ? ratingInput.value : '';
    const consent = !!(consentInput && consentInput.checked);
    const claimedReviewerName = (reviewerNameInput?.value || '').trim();

    // Validation tiers:
    // 1. review_form actions: rating + (location, optionally order) is
    //    the testimony — link/notes/file are all optional.
    // 2. requires_text actions (Store & Experience): notes are mandatory
    //    on top of the rating.
    // 3. everything else (sosyal medya post linkleri vs.): at least one
    //    of url/notes/file is required so staff has something to verify.
    const requiresText = !!(form.dataset.requiresText === '1');
    const optionalOrder = !!(form.dataset.optionalOrder === '1');

    if (url && !/^https?:\/\//i.test(url)) {
      showSubmitMsg(msg, 'URL must start with http:// or https://', 'is-err');
      return;
    }
    if (isReview) {
      if (!rating) {
        showSubmitMsg(msg, 'Tap a star to give this experience a rating.', 'is-err');
        return;
      }
      if (!locationId) {
        showSubmitMsg(msg, 'Pick which store this is about.', 'is-err');
        return;
      }
      if (!optionalOrder && !orderId) {
        showSubmitMsg(msg, 'Pick which order this review is about.', 'is-err');
        return;
      }
      if (requiresText && !notes) {
        showSubmitMsg(msg, 'Tell us about your experience — a few sentences please.', 'is-err');
        return;
      }
      if (consentInput && consentInput.required && !consent) {
        showSubmitMsg(msg, 'Please confirm that you are okay with us publishing this review on the public reviews page.', 'is-err');
        return;
      }
      if (isGbpMatch && !claimedReviewerName) {
        showSubmitMsg(msg, "Please enter the name you'll use on Google so we can match your review.", 'is-err');
        return;
      }
    } else if (!url && !notes && !hasFile) {
      showSubmitMsg(msg, 'Add a link, a screenshot, or a quick description.', 'is-err');
      return;
    }

    btn.disabled = true;
    btn.textContent = 'Submitting...';
    try {
      let result;
      if (hasFile) {
        const fd = new FormData();
        fd.append('action_key', actionKey);
        if (url) fd.append('proof_url', url);
        if (notes) fd.append('notes', notes);
        if (orderId) fd.append('order_id', orderId);
        if (locationId) fd.append('location_id', locationId);
        if (rating) fd.append('rating', rating);
        if (consent) fd.append('publish_consent', '1');
        if (claimedReviewerName) fd.append('claimed_reviewer_name', claimedReviewerName);
        fd.append('screenshot', fileInput.files[0]);
        result = await apiPostMultipart('/social/submit', fd);
      } else {
        const payload = { action_key: actionKey };
        if (url) payload.proof_url = url;
        if (notes) payload.notes = notes;
        if (orderId) payload.order_id = parseInt(orderId, 10);
        if (locationId) payload.location_id = parseInt(locationId, 10);
        if (rating) payload.rating = parseInt(rating, 10);
        if (consent) payload.publish_consent = true;
        if (claimedReviewerName) payload.claimed_reviewer_name = claimedReviewerName;
        result = await apiPost('/social/submit', payload);
      }
      showSubmitMsg(msg, result.message || 'Submitted! Our team will review it.', 'is-ok');
      if (urlInput) urlInput.value = '';
      if (notesInput) notesInput.value = '';
      if (fileInput) fileInput.value = '';
      if (orderSelect) orderSelect.value = '';
      if (locSelect) locSelect.value = '';
      if (ratingInput) ratingInput.value = '';
      if (consentInput) consentInput.checked = false;
      if (reviewerNameInput) reviewerNameInput.value = '';
      form.querySelectorAll('.coin-star-btn.is-active').forEach(b => b.classList.remove('is-active'));
      // Refresh context so the just-reviewed order appears as disabled.
      loadSocialContext().then(loadSocialActions);
      loadSocialSubmissions();
      loadBalance();
    } catch (e) {
      showSubmitMsg(msg, 'Error: ' + e.message, 'is-err');
    } finally {
      btn.disabled = false;
      btn.textContent = 'Submit for review';
    }
  };

  function showSubmitMsg(el, text, cls) {
    if (!el) return;
    el.style.display = 'block';
    el.className = 'coin-action-submit-msg ' + cls;
    el.textContent = text;
  }

  // Load profile
  async function loadProfile() {
    try {
      const data = await apiGet('/profile');
      const fields = data.fields || [];

      document.getElementById('profileProgressBar').style.width = data.completion_percent + '%';
      document.getElementById('profileProgressText').textContent = `${data.completion_percent}% Complete — ${data.total_earned}/${data.lifetime_limit} coins earned`;

      const container = document.getElementById('profileFields');
      container.innerHTML = fields.map(f => {
        if (f.completed) {
          return `<div class="coin-profile-field completed">
            <label>${formatFieldName(f.field)} <span class="coin-field-reward">✓ ${f.coins} coins earned</span></label>
            <span>${formatFieldDisplayValue(f) || '—'}</span>
          </div>`;
        }

        let input;
        if (f.options) {
          input = `<select onchange="saveProfileField('${f.field}', this.value)">
            <option value="">Select...</option>
            ${f.options.map(o => `<option value="${o}"${f.value === o ? ' selected' : ''}>${o}</option>`).join('')}
          </select>`;
        } else if (f.field === 'birthday') {
          input = renderBirthdayInput(f.value);
        } else if (f.field === 'email_sms_consent') {
          // Phone verification via Twilio Verify — two-step OTP flow.
          //   Step 1: customer submits their phone → backend calls Twilio
          //           sendCode → Twilio texts a 6-digit code.
          //   Step 2: customer types the code into the OTP input below →
          //           backend calls Twilio checkCode → on `approved` we
          //           set phone_verified_at, credit coins and unlock the
          //           Reviews & Social Media rewards.
          // refreshSmsStatus() resumes the right step on page reload.
          input = `
            <div class="coin-action-form" data-sms-consent>
              <div class="coin-sms-status" id="smsStatusBox" style="display:none"></div>
              <div class="coin-phone-field" id="smsInputRow">
                <label class="coin-phone-field__label" for="phoneInput_${f.field}">Mobile number</label>
                <div class="coin-phone-field__input">
                  <span class="coin-phone-field__prefix" aria-hidden="true">
                    <span class="coin-phone-field__flag">🇺🇸</span>
                    <span class="coin-phone-field__country">+1</span>
                  </span>
                  <input type="tel" id="phoneInput_${f.field}" placeholder="(555) 123-4567" autocomplete="tel-national" inputmode="tel">
                </div>
                <button type="button" class="coin-btn coin-btn-primary coin-phone-field__submit" onclick="submitConsentPhone(this)">
                  Send 6-digit code
                </button>
              </div>
              <div class="coin-phone-field" id="smsCodeRow" style="display:none">
                <label class="coin-phone-field__label" for="smsCodeInput">Enter the 6-digit code</label>
                <div class="coin-phone-field__input coin-phone-field__input--code">
                  <input type="text" id="smsCodeInput" placeholder="••••••" inputmode="numeric" autocomplete="one-time-code" maxlength="10">
                </div>
                <button type="button" class="coin-btn coin-btn-primary coin-phone-field__submit" onclick="submitVerificationCode(this)">
                  Verify
                </button>
              </div>
              <small class="coin-form-helper">
                We'll text you a 6-digit code. Enter it here to verify your phone and unlock <strong>+${f.coins} coins</strong> plus the Reviews &amp; Social Media rewards. Standard carrier rates may apply.
              </small>
            </div>`;
        } else if (f.field === 'unboxing_video_url') {
          input = `
            <div class="coin-action-form">
              <input type="url" id="unboxingInput" placeholder="https://instagram.com/reel/... or https://youtube.com/...">
              <button type="button" class="coin-btn coin-btn-primary" onclick="submitUnboxingVideo()">Submit for review</button>
              <small class="coin-form-helper">Coins are released after our team verifies the video.</small>
            </div>`;
        } else if (f.field === 'mobile_app_installed') {
          input = `<button class="coin-btn coin-btn-primary" onclick="saveProfileField('${f.field}', true)">Confirm</button>`;
        } else {
          input = `<input type="text" placeholder="Enter..." onchange="saveProfileField('${f.field}', this.value)">`;
        }

        return `<div class="coin-profile-field">
          <label>${formatFieldName(f.field)} <span class="coin-field-reward">+${f.coins} coins</span></label>
          ${input}
        </div>`;
      }).join('');

      // After the field markup is in the DOM we ask the backend whether the
      // customer is already mid-verification or done — `submitConsentPhone`
      // could have run earlier in the session.
      refreshSmsStatus();
    } catch (e) {
      document.getElementById('profileFields').innerHTML = '<p>Failed to load profile.</p>';
    }
  }

  window.saveProfileField = async function(field, value) {
    if (!value) return;
    try {
      const result = await apiPost('/profile', { field, value });
      if (result.success) {
        loadProfile();
        loadBalance();
      }
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  // Birthday — three lightweight dropdowns (month, day, year). Auto-saves
  // once all three are picked; cleaner than `<input type="date">` on
  // mobile Safari and gives clearer feedback that the field persisted.
  // Pre-populates from a previously-saved value (ISO YYYY-MM-DD) so the
  // customer can see their birthday after page reload — even if the coin
  // award didn't fire (e.g. trust gate / lifetime cap).
  function renderBirthdayInput(savedValue) {
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const days = Array.from({length: 31}, (_, i) => i + 1);
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear - 13; y >= currentYear - 100; y--) years.push(y);

    let savedM = '', savedD = '', savedY = '';
    if (savedValue) {
      const m = String(savedValue).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) {
        savedY = parseInt(m[1], 10);
        savedM = parseInt(m[2], 10);
        savedD = parseInt(m[3], 10);
      }
    }

    const monthOpts = months.map((mLabel, i) => {
      const v = i + 1;
      return `<option value="${v}"${v === savedM ? ' selected' : ''}>${mLabel}</option>`;
    }).join('');
    const dayOpts = days.map(d => `<option value="${d}"${d === savedD ? ' selected' : ''}>${d}</option>`).join('');
    const yearOpts = years.map(y => `<option value="${y}"${y === savedY ? ' selected' : ''}>${y}</option>`).join('');

    const savedHint = savedValue
      ? '<div class="coin-field-saved" style="display:block;color:var(--coin-success,#15803d);">Saved ✓</div>'
      : '<div class="coin-field-saved" style="display:none">Saved ✓</div>';

    return `
      <div class="coin-birthday-row" data-bday>
        <select data-bday-part="month" aria-label="Month" onchange="window.maybeSaveBirthday(this)">
          <option value="">Month</option>${monthOpts}
        </select>
        <select data-bday-part="day" aria-label="Day" onchange="window.maybeSaveBirthday(this)">
          <option value="">Day</option>${dayOpts}
        </select>
        <select data-bday-part="year" aria-label="Year" onchange="window.maybeSaveBirthday(this)">
          <option value="">Year</option>${yearOpts}
        </select>
      </div>
      ${savedHint}`;
  }

  // Render a profile field's stored value for the "completed" state. Some
  // fields (birthday, dates) need humanizing; everything else falls
  // through to the raw value.
  function formatFieldDisplayValue(f) {
    if (f.field === 'birthday' && f.value) {
      const m = String(f.value).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) {
        const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        return `${months[parseInt(m[2],10)-1]} ${parseInt(m[3],10)}, ${m[1]}`;
      }
    }
    return f.value;
  }

  window.maybeSaveBirthday = async function(sel) {
    const wrap = sel.closest('[data-bday]');
    if (!wrap) return;
    const m = wrap.querySelector('[data-bday-part="month"]').value;
    const d = wrap.querySelector('[data-bday-part="day"]').value;
    const y = wrap.querySelector('[data-bday-part="year"]').value;
    if (!m || !d || !y) return;
    const iso = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const saved = wrap.parentElement.querySelector('.coin-field-saved');
    try {
      const result = await apiPost('/profile', { field: 'birthday', value: iso });
      if (result.success) {
        if (saved) saved.style.display = 'block';
        setTimeout(() => { loadProfile(); loadBalance(); }, 800);
      }
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  // Email/SMS Consent — Twilio Verify (two-step OTP).
  //
  // Step 1: phone → POST /profile field=email_sms_consent
  //         → backend calls Twilio sendCode → SMS arrives with 6-digit code
  // Step 2: code  → POST /sms/verify
  //         → backend calls Twilio checkCode → on approved: phone_verified_at
  //           is set + 25 coins credited + trust score recomputed.
  window.submitConsentPhone = async function(btn) {
    const input = document.getElementById('phoneInput_email_sms_consent');
    const statusBox = document.getElementById('smsStatusBox');
    if (!input) return;
    const value = (input.value || '').trim();
    if (!value) {
      showSmsStatus(statusBox, 'Enter your mobile number first.', 'is-err');
      return;
    }
    if (!/^[0-9+()\-\s]{7,}$/.test(value)) {
      showSmsStatus(statusBox, 'That phone number does not look right.', 'is-err');
      return;
    }

    btn.disabled = true;
    btn.textContent = 'Sending…';
    try {
      const result = await apiPost('/profile', { field: 'email_sms_consent', value });

      if (result?.already_verified) {
        renderSmsVerified(statusBox);
      } else if (result?.pending_verification || result?.success) {
        renderSmsCodePrompt(statusBox, result.phone || value);
      }
    } catch (e) {
      showSmsStatus(statusBox, e.message || 'Could not send code.', 'is-err');
    } finally {
      btn.disabled = false;
      btn.textContent = 'Send 6-digit code';
    }
  };

  // Step 2 — confirm the OTP. Twilio responds approved/pending; we surface
  // each clearly so the customer never has to guess what went wrong.
  window.submitVerificationCode = async function(btn) {
    const input = document.getElementById('smsCodeInput');
    const statusBox = document.getElementById('smsStatusBox');
    if (!input) return;
    const code = (input.value || '').replace(/\D/g, '');
    if (code.length < 4) {
      showSmsStatus(statusBox, 'Enter the 6-digit code we just sent.', 'is-err');
      return;
    }

    btn.disabled = true;
    btn.textContent = 'Verifying…';
    try {
      const result = await apiPost('/sms/verify', { code });
      if (result?.verified) {
        renderSmsVerified(statusBox);
        loadProfile();
        loadBalance();
      } else {
        showSmsStatus(statusBox, result?.message || 'That code is incorrect.', 'is-err');
      }
    } catch (e) {
      showSmsStatus(statusBox, e.message || 'Could not verify code.', 'is-err');
    } finally {
      btn.disabled = false;
      btn.textContent = 'Verify';
    }
  };

  // "Didn't get it?" → re-trigger Twilio sendCode for the saved phone.
  window.resendVerificationCode = async function(btn) {
    const statusBox = document.getElementById('smsStatusBox');
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Sending…';
    }
    try {
      const result = await apiPost('/sms/resend', {});
      if (result?.success) {
        showSmsStatus(statusBox, 'New code sent. Check your phone.', 'is-pending');
      } else {
        showSmsStatus(statusBox, result?.message || 'Could not resend.', 'is-err');
      }
    } catch (e) {
      showSmsStatus(statusBox, e.message || 'Could not resend.', 'is-err');
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.textContent = 'Resend code';
      }
    }
  };

  // Pending state: phone submitted, code expected. Hide phone row, show
  // OTP row, status banner explains where the SMS went.
  function renderSmsCodePrompt(box, phone) {
    const inputRow = document.getElementById('smsInputRow');
    const codeRow = document.getElementById('smsCodeRow');
    if (inputRow) inputRow.style.display = 'none';
    if (codeRow) codeRow.style.display = '';
    if (!box) return;
    box.style.display = 'block';
    box.className = 'coin-sms-status is-pending';
    box.innerHTML = `
      <strong>Check your phone.</strong>
      We just texted a 6-digit code to ${escapeHtml(phone)}. Enter it below to verify.
      <div style="margin-top:8px;display:flex;gap:8px;flex-wrap:wrap;">
        <button type="button" class="coin-btn coin-btn-secondary coin-sms-resend" onclick="resendVerificationCode(this)">
          Resend code
        </button>
        <button type="button" class="coin-btn coin-btn-secondary coin-sms-resend" onclick="resetSmsForm()">
          Use a different number
        </button>
      </div>`;
    // Help mobile Safari focus the OTP input automatically so the iOS
    // SMS-from-keyboard suggestion appears.
    setTimeout(() => {
      const codeInput = document.getElementById('smsCodeInput');
      if (codeInput) codeInput.focus();
    }, 100);
  }

  function renderSmsVerified(box) {
    if (!box) return;
    const inputRow = document.getElementById('smsInputRow');
    const codeRow = document.getElementById('smsCodeRow');
    if (inputRow) inputRow.style.display = 'none';
    if (codeRow) codeRow.style.display = 'none';
    box.style.display = 'block';
    box.className = 'coin-sms-status is-ok';
    box.innerHTML = `<strong>Phone verified ✓</strong> Reviews &amp; Social Media rewards are now unlocked.`;
  }

  function showSmsStatus(box, message, cls) {
    if (!box) return;
    box.style.display = 'block';
    box.className = 'coin-sms-status ' + (cls || '');
    box.textContent = message;
  }

  window.resetSmsForm = function() {
    const inputRow = document.getElementById('smsInputRow');
    const codeRow = document.getElementById('smsCodeRow');
    const statusBox = document.getElementById('smsStatusBox');
    const codeInput = document.getElementById('smsCodeInput');
    if (inputRow) inputRow.style.display = '';
    if (codeRow) codeRow.style.display = 'none';
    if (codeInput) codeInput.value = '';
    if (statusBox) {
      statusBox.style.display = 'none';
      statusBox.innerHTML = '';
    }
  };

  // Resume the right step on page load: verified → green banner; pending
  // (code already sent earlier in this session) → re-render OTP prompt.
  async function refreshSmsStatus() {
    const statusBox = document.getElementById('smsStatusBox');
    if (!statusBox) return;
    try {
      const data = await apiGet('/sms/status');
      if (data?.verified) {
        renderSmsVerified(statusBox);
      } else if (data?.pending) {
        renderSmsCodePrompt(statusBox, data.pending_phone || 'your phone');
      }
    } catch (e) {
      // No-op — box stays in the default input state.
    }
  }

  // Unboxing Video URL — submits a pending submission for admin approval.
  window.submitUnboxingVideo = async function() {
    const input = document.getElementById('unboxingInput');
    if (!input) return;
    const url = (input.value || '').trim();
    if (!url) {
      alert('Paste the URL of your unboxing video.');
      return;
    }
    if (!/^https?:\/\//i.test(url)) {
      alert('URL must start with http:// or https://');
      return;
    }

    try {
      await apiPost('/social/submit', {
        action_key: 'profile_unboxing_video_url',
        proof_url: url,
      });
      alert("Thanks! Your video is queued for review. We'll release coins once it's verified.");
      input.value = '';
      loadSocialSubmissions();
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  // Store & Experience — pending submission with optional order linked.
  document.getElementById('storeExperienceText')?.addEventListener('input', function() {
    const counter = document.getElementById('storeExperienceCount');
    if (counter) counter.textContent = this.value.length;
  });
  document.getElementById('storeExperienceSubmit')?.addEventListener('click', async function() {
    const textEl = document.getElementById('storeExperienceText');
    const orderEl = document.getElementById('storeExperienceOrder');
    const statusEl = document.getElementById('storeExperienceStatus');
    if (!textEl) return;
    const text = (textEl.value || '').trim();
    if (text.length < 10) {
      alert('Please share at least 10 characters about your visit.');
      return;
    }

    this.disabled = true;
    if (statusEl) statusEl.textContent = 'Submitting...';
    try {
      // The story goes in `notes` (which is what submitSocial reads to
      // satisfy the requires_text rule). The dropdown values are
      // Shopify customer-facing order numbers (e.g. 4072), not local
      // DB ids — send them as `order_number` so the backend can
      // resolve them against orders.order_number for the active
      // customer.
      const orderNumber = orderEl?.value ? String(orderEl.value).replace(/^#/, '') : '';
      const payload = {
        action_key: 'profile_store_experience',
        notes: text,
      };
      if (orderNumber) payload.order_number = parseInt(orderNumber, 10);
      await apiPost('/social/submit', payload);
      if (statusEl) {
        statusEl.className = 'coin-action-status is-pending';
        statusEl.textContent = "Thanks! We'll review and release +10 coins once approved.";
      }
      textEl.value = '';
      if (orderEl) orderEl.value = '';
      const counter = document.getElementById('storeExperienceCount');
      if (counter) counter.textContent = '0';
    } catch (e) {
      if (statusEl) {
        statusEl.className = 'coin-action-status';
        statusEl.textContent = 'Error: ' + e.message;
      }
    } finally {
      this.disabled = false;
    }
  });

  // Load referral
  async function loadReferral() {
    try {
      const data = await apiGet('/referral');
      const shopDomain = window.Shopify?.shop || 'www.dtftown.com';
      document.getElementById('referralLink').value = `https://${shopDomain}?ref=${data.referral_code}`;

      document.getElementById('referralStats').innerHTML = `
        <div class="coin-detail"><span class="coin-detail-label">Referred</span><span class="coin-detail-value">${data.total_referred}</span></div>
        <div class="coin-detail"><span class="coin-detail-label">Rewarded</span><span class="coin-detail-value">${data.rewarded}</span></div>
        <div class="coin-detail"><span class="coin-detail-label">Pending</span><span class="coin-detail-value">${data.pending}</span></div>
        <div class="coin-detail"><span class="coin-detail-label">Coins Earned</span><span class="coin-detail-value">${data.total_coins_earned}</span></div>
        <div class="coin-detail"><span class="coin-detail-label">Slots Left</span><span class="coin-detail-value">${data.remaining_slots}</span></div>
      `;
    } catch (e) {
      console.error('Failed to load referral:', e);
    }
  }

  // Copy referral link
  document.getElementById('copyReferralBtn')?.addEventListener('click', function() {
    const input = document.getElementById('referralLink');
    navigator.clipboard.writeText(input.value).then(() => {
      this.textContent = 'Copied!';
      setTimeout(() => this.textContent = 'Copy', 2000);
    });
  });

  // Load surveys
  async function loadSurveys() {
    try {
      const data = await apiGet('/surveys');
      const list = document.getElementById('surveysList');
      const surveys = data.surveys || [];

      if (!surveys.length) {
        list.innerHTML = '<p class="text-muted">No surveys available at this time.</p>';
        return;
      }

      list.innerHTML = surveys.map(s => `
        <div class="coin-action-card">
          <h4>${s.title}</h4>
          <div class="coin-action-coins">${s.coin_reward} coins</div>
          <small>${s.question_count} questions</small>
          <div style="margin-top:.5rem">
            <button class="coin-btn coin-btn-primary" onclick="alert('Survey UI coming soon')">Take Survey</button>
          </div>
        </div>
      `).join('');
    } catch (e) {
      document.getElementById('surveysList').innerHTML = '<p>Failed to load surveys.</p>';
    }
  }

  // Load campaigns
  async function loadCampaigns() {
    try {
      const data = await apiGet('/campaigns');
      const grid = document.getElementById('campaignsGrid');
      const campaigns = data.campaigns || [];

      if (!campaigns.length) {
        grid.innerHTML = '<p class="coin-loading">No active campaigns right now. Check back soon!</p>';
        return;
      }

      grid.innerHTML = campaigns.map(c => {
        const endsDate = new Date(c.ends_at).toLocaleDateString();
        return `
          <div class="coin-campaign-card">
            <h4>${c.name}</h4>
            <p>${c.type === 'multiplier' ? c.multiplier + 'x coin multiplier' : '+' + c.bonus_coins + ' bonus coins'}</p>
            <small>Ends: ${endsDate} • Pool: ${c.pool_lifetime_days} days</small>
          </div>
        `;
      }).join('');
    } catch (e) {
      document.getElementById('campaignsGrid').innerHTML = '<p>Failed to load campaigns.</p>';
    }
  }

  // Redeem modal
  // Reset the form to its pristine state so a customer can (deliberately)
  // generate another code after closing/reopening the modal. This also
  // clears the "locked" state we apply after a successful redemption.
  function resetRedeemForm() {
    const result = document.getElementById('redeemResult');
    const confirmBtn = document.getElementById('confirmRedeem');
    const amountInput = document.getElementById('redeemAmount');
    if (result) { result.style.display = 'none'; result.innerHTML = ''; }
    if (confirmBtn) {
      confirmBtn.style.display = '';
      confirmBtn.disabled = false;
      confirmBtn.textContent = 'Get Discount Code';
    }
    if (amountInput) { amountInput.disabled = false; amountInput.value = ''; }
    const dollar = document.getElementById('redeemDollarValue');
    if (dollar) dollar.textContent = '= $0.00';
  }

  document.getElementById('redeemBtn')?.addEventListener('click', () => {
    resetRedeemForm();
    document.getElementById('redeemModal').style.display = 'flex';
  });
  document.getElementById('closeRedeemModal')?.addEventListener('click', () => {
    document.getElementById('redeemModal').style.display = 'none';
  });

  document.getElementById('redeemAmount')?.addEventListener('input', function() {
    const val = parseInt(this.value) || 0;
    document.getElementById('redeemDollarValue').textContent = `= $${(val / 10).toFixed(2)}`;
  });

  // Copy a generated discount code to the clipboard. Exposed globally so the
  // inline button rendered into #redeemResult can call it.
  window.coinCopyCode = function(btn, code) {
    const done = () => {
      btn.textContent = 'Copied!';
      setTimeout(() => { btn.textContent = 'Copy code'; }, 2000);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done).catch(() => {
        window.prompt('Copy your discount code:', code);
      });
    } else {
      window.prompt('Copy your discount code:', code);
    }
  };

  document.getElementById('confirmRedeem')?.addEventListener('click', async function() {
    const amountInput = document.getElementById('redeemAmount');
    const amount = parseInt(amountInput.value);
    if (!amount || amount < 50) {
      alert('Minimum 50 coins required.');
      return;
    }

    // Lock the button up-front so a double-click / impatient second click
    // can never mint two codes (each redemption spends coins).
    this.disabled = true;
    this.textContent = 'Generating…';

    try {
      const result = await apiPost('/redeem', { amount });
      const el = document.getElementById('redeemResult');
      const safeCode = escapeHtml(result.discount_code);
      el.style.display = 'block';
      el.innerHTML = `
        <div style="background:#f0fdf4;padding:1rem;border-radius:8px;margin-top:1rem;text-align:center">
          <strong>Your discount code:</strong><br>
          <code style="font-size:1.5rem;font-weight:800;color:#1b7a43" id="redeemCodeValue">${safeCode}</code><br>
          <button type="button" class="coin-btn coin-btn-primary" style="margin:0.75rem 0 0.25rem"
                  onclick="coinCopyCode(this, '${safeCode}')">Copy code</button><br>
          <small>Value: $${result.dollar_value} — Expires in 24h. We also emailed it to you.</small><br>
          <small>Apply this code at checkout.</small>
        </div>
      `;
      // Prevent accidental re-generation: hide the confirm button and lock
      // the amount input. The customer can reopen the modal to redeem again.
      this.style.display = 'none';
      if (amountInput) amountInput.disabled = true;
      loadBalance();
    } catch (e) {
      // Re-enable so the customer can retry (their coins are untouched on error).
      this.disabled = false;
      this.textContent = 'Get Discount Code';
      alert('Error: ' + e.message);
    }
  });

  function formatFieldName(field) {
    return field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  // Init — load context first so action cards have orders/locations
  // available the moment loadSocialActions paints the dropdowns.
  loadSocialContext().then(() => {
    loadSocialActions();
    loadSocialSubmissions();
  });
  loadBalance();
  loadTierProgress();
  loadProfile();
  loadReferral();
  loadSurveys();
  loadCampaigns();
})();
